import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  DollarSign,
  Calculator,
  Send,
  Check,
  XCircle,
  Trash2,
  Download,
  FileText,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { PermissionGate } from '@/components/common/PermissionGate'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { usePermissions } from '@/hooks/usePermissions'
import { useCompany } from '@/features/companies/hooks/useCompanies'
import {
  usePayrollRun,
  usePayrollRunPreflight,
  usePayrollRunItems,
  useCalculateRun,
  useSubmitRun,
  useApproveRun,
  useRejectRun,
  useVoidRun,
} from '@/features/payroll/hooks/usePayrollRuns'
import { StatusBadge } from './StatusBadge'
import { StatCard } from './StatCard'
import { PreflightPanel } from './PreflightPanel'
import { RejectionInfo } from './RejectionInfo'
import { RejectReasonModal } from './RejectReasonModal'
import { PayrollFxRequirementsCard } from './PayrollFxRequirementsCard'
import { CreateExchangeRateModal } from './CreateExchangeRateModal'
import { downloadRunPreview } from '@/api/services/payroll-runs.service'
import type {
  MissingExchangeRateRequirement,
  PayrollFxRequirements,
  PayrollItem,
} from '@/features/payroll/types/payroll.types'
import {
  getFxRequirementsFromCalculateError,
  getFxRequirementsFromPreflight,
} from '@/features/payroll/utils/fxRequirements'

const RUN_TYPE_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'danger'> = {
  batch: 'neutral',
  off_cycle: 'info',
  correction: 'warning',
  final_settlement: 'danger',
}

function getEmployeeName(emp: PayrollItem['employeeId']): string {
  if (typeof emp === 'string') return emp
  return [emp.firstName, emp.lastName].filter(Boolean).join(' ') || emp.email || emp._id
}

export function RunDetailPage(): React.ReactElement {
  const { companyId, runId } = useParams<{ companyId: string; runId: string }>()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { data: company } = useCompany(companyId ?? null)
  const companyName = company?.name ?? 'Company'
  const [page, setPage] = useState(1)
  const [confirmAction, setConfirmAction] = useState<'approve' | 'void' | 'submit' | null>(null)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [exchangeRateTarget, setExchangeRateTarget] =
    useState<MissingExchangeRateRequirement | null>(null)
  const [calculateFxFallback, setCalculateFxFallback] =
    useState<PayrollFxRequirements | null>(null)

  const { data: run, isLoading: runLoading, isError: runError } = usePayrollRun(companyId ?? null, runId ?? null)
  const {
    data: preflight,
    isLoading: preflightLoading,
    refetch: refetchPreflight,
  } = usePayrollRunPreflight(companyId ?? null, runId ?? null)
  const { data: itemsData, isLoading: itemsLoading } = usePayrollRunItems(companyId ?? null, runId ?? null, page)

  const calculateMut = useCalculateRun()
  const submitMut = useSubmitRun()
  const approveMut = useApproveRun()
  const rejectMut = useRejectRun()
  const voidMut = useVoidRun()

  const anyMutating =
    calculateMut.isPending || submitMut.isPending || approveMut.isPending || rejectMut.isPending || voidMut.isPending

  if (!companyId || !runId) {
    navigate('/companies', { replace: true })
    return <></>
  }

  if (runLoading) {
    return (
      <PageShell
        breadcrumbItems={[
          { label: 'Companies', to: '/companies' },
          { label: companyName, to: `/companies/${companyId}` },
          { label: 'Payroll', to: `/companies/${companyId}/payroll/runs` },
          { label: 'Run' },
        ]}
        icon={DollarSign}
        title="Payroll Run"
        className="gap-0"
        hideHeader
      >
        <TableSkeleton rows={5} cols={6} />
      </PageShell>
    )
  }

  if (runError || !run) {
    return (
      <PageShell
        breadcrumbItems={[
          { label: 'Companies', to: '/companies' },
          { label: companyName, to: `/companies/${companyId}` },
          { label: 'Payroll', to: `/companies/${companyId}/payroll/runs` },
          { label: 'Not Found' },
        ]}
        icon={DollarSign}
        title="Payroll Run"
        className="gap-0"
        hideHeader
      >
        <EmptyState icon={XCircle} title="Payroll run not found" />
      </PageShell>
    )
  }

  const period = `${run.payrollMonth.year}-${String(run.payrollMonth.month).padStart(2, '0')}`
  const status = run.status
  const isEditable = status === 'draft' || status === 'calculated'
  const items = itemsData?.data ?? []
  const preflightFxRequirements = getFxRequirementsFromPreflight(preflight)
  const showCalculateFxFallback =
    !!calculateFxFallback && !preflightFxRequirements?.missing.length
  const canCreateExchangeRate = can('tax_rules', 'create')
  const hasBlockingPreflightIssues = preflightLoading || preflight?.canRun === false
  const calculateDisabledTitle = preflightLoading
    ? 'Checking payroll readiness...'
    : preflight?.canRun === false
      ? 'Resolve blocking preflight issues before calculating payroll.'
      : undefined

  const handleConfirm = (): void => {
    if (!confirmAction) return
    const args = { companyId, runId }
    if (confirmAction === 'approve') approveMut.mutate(args, { onSuccess: () => setConfirmAction(null) })
    if (confirmAction === 'void') voidMut.mutate(args, { onSuccess: () => setConfirmAction(null) })
    if (confirmAction === 'submit') submitMut.mutate(args, { onSuccess: () => setConfirmAction(null) })
  }

  const handleReject = (reason: string): void => {
    rejectMut.mutate({ companyId, runId, reason }, { onSuccess: () => setRejectOpen(false) })
  }

  const handleDownload = async (format: 'pdf' | 'csv'): Promise<void> => {
    setDownloading(true)
    try {
      const blob = await downloadRunPreview(companyId, runId, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `payroll-${period}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }

  const handleCreateExchangeRate = (requirement: MissingExchangeRateRequirement): void => {
    setExchangeRateTarget(requirement)
  }

  const handleExchangeRateCreated = async (): Promise<void> => {
    setCalculateFxFallback(null)
    await refetchPreflight()
  }

  const handleCalculate = (): void => {
    setCalculateFxFallback(null)
    calculateMut.mutate(
      { companyId, runId },
      {
        onSuccess: () => {
          setCalculateFxFallback(null)
        },
        onError: (error) => {
          setCalculateFxFallback(
            getFxRequirementsFromCalculateError(
              error,
              preflightFxRequirements?.baseCurrency ?? run.baseCurrency,
              preflightFxRequirements?.asOf,
            ),
          )
        },
      },
    )
  }

  const confirmLabels: Record<string, { title: string; message: string; label: string }> = {
    approve: { title: 'Approve Run', message: 'This run will be marked as approved and ready for org-level consolidation.', label: 'Approve' },
    void: { title: 'Void Run', message: 'This will void the run and all its items. This action cannot be undone.', label: 'Void Run' },
    submit: { title: 'Submit for Approval', message: 'This will submit the run for manager approval. You cannot modify it after submission.', label: 'Submit' },
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Companies', to: '/companies' },
        { label: companyName, to: `/companies/${companyId}` },
        { label: 'Payroll', to: `/companies/${companyId}/payroll/runs` },
        { label: `Run ${period}` },
      ]}
      icon={DollarSign}
      title={`Payroll Run — ${period}`}
      description={`${run.employeeCount} employees · ${run.currency}`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <>
              <StatusBadge status={status} />
              <Badge variant={RUN_TYPE_VARIANT[run.runType] ?? 'neutral'}>{run.runType.replace('_', ' ')}</Badge>
            </>
          }
        />

        {/* Rejection info */}
        {run.rejectedReason && (
          <RejectionInfo reason={run.rejectedReason} rejectedAt={run.rejectedAt} />
        )}

        {/* Calculation errors */}
        {run.hasErrors && run.calculationErrors.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
            <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-200">
              <AlertTriangle className="h-4 w-4" />
              {run.calculationErrors.length} employee(s) failed during calculation
            </div>
            <ul className="mt-2 space-y-1 pl-6 text-sm text-red-700 dark:text-red-300">
              {run.calculationErrors.map((err, i) => (
                <li key={i}>
                  {err.employeeName ?? err.employeeId}: {err.errorMessage}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preflight */}
        {preflight && isEditable && (
          <PreflightPanel
            preflight={preflight}
            defaultCollapsed={status === 'calculated'}
            onCreateExchangeRate={
              canCreateExchangeRate ? handleCreateExchangeRate : undefined
            }
          />
        )}

        {showCalculateFxFallback && calculateFxFallback && (
          <PayrollFxRequirementsCard
            title="Calculation blocked by missing exchange rates"
            requirements={calculateFxFallback}
            tone="danger"
            onCreateExchangeRate={
              canCreateExchangeRate ? handleCreateExchangeRate : undefined
            }
          />
        )}

        {/* Totals */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Gross Pay" value={run.totals?.grossPay ?? 0} currency={run.currency} />
          <StatCard label="Net Pay" value={run.totals?.netPay ?? 0} currency={run.currency} />
          <StatCard label="Total Deductions" value={run.totals?.totalDeductions ?? 0} currency={run.currency} />
          <StatCard label="Employees" value={run.employeeCount} />
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          {/* Calculate / Recalculate */}
          <PermissionGate module="payroll" action="run">
            {isEditable && (
              <Button
                variant="primary"
                size="sm"
                disabled={anyMutating || hasBlockingPreflightIssues}
                title={calculateDisabledTitle}
                onClick={handleCalculate}
              >
                <Calculator className="mr-1.5 h-3.5 w-3.5" />
                {status === 'calculated' ? 'Recalculate' : 'Calculate'}
              </Button>
            )}
          </PermissionGate>

          {/* Submit */}
          <PermissionGate module="payroll" action="submit">
            {status === 'calculated' && !run.hasErrors && (
              <Button variant="primary" size="sm" disabled={anyMutating} onClick={() => setConfirmAction('submit')}>
                <Send className="mr-1.5 h-3.5 w-3.5" />
                Submit for Approval
              </Button>
            )}
            {status === 'calculated' && run.hasErrors && (
              <Button variant="secondary" size="sm" disabled title="Fix calculation errors first">
                <Send className="mr-1.5 h-3.5 w-3.5" />
                Submit for Approval
              </Button>
            )}
          </PermissionGate>

          {/* Approve / Reject: granted separately */}
          <PermissionGate module="payroll" action="approve">
            {status === 'pending' && (
              <Button variant="primary" size="sm" disabled={anyMutating} onClick={() => setConfirmAction('approve')}>
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Approve
              </Button>
            )}
          </PermissionGate>
          <PermissionGate module="payroll" action="reject">
            {status === 'pending' && (
              <Button variant="danger" size="sm" disabled={anyMutating} onClick={() => setRejectOpen(true)}>
                <XCircle className="mr-1.5 h-3.5 w-3.5" />
                Reject
              </Button>
            )}
          </PermissionGate>

          {/* Void */}
          <PermissionGate module="payroll" action="adjust">
            {status === 'approved' && (
              <>
                <Button variant="danger" size="sm" disabled={anyMutating} onClick={() => setConfirmAction('void')}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Void
                </Button>
                <Link to={`/companies/${companyId}/payroll/runs/${runId}/adjust`}>
                  <Button variant="secondary" size="sm">
                    <FileText className="mr-1.5 h-3.5 w-3.5" />
                    Create Adjustment
                  </Button>
                </Link>
              </>
            )}
          </PermissionGate>

          {/* Preview / Export — shown after calculation */}
          {status !== 'draft' && (
            <div className="relative ml-auto">
              <Button
                variant="secondary"
                size="sm"
                disabled={downloading}
                onClick={() => handleDownload('pdf')}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                {downloading ? '...' : 'PDF'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="ml-1"
                disabled={downloading}
                onClick={() => handleDownload('csv')}
              >
                <ChevronDown className="mr-1.5 h-3.5 w-3.5" />
                CSV
              </Button>
            </div>
          )}
        </div>

        {/* Items table */}
        {itemsLoading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Calculator}
            title="No payroll items yet"
            description="Run calculation to generate employee payroll items."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3 text-right">Gross</th>
                    <th className="px-4 py-3 text-right">Deductions</th>
                    <th className="px-4 py-3 text-right">Tax</th>
                    <th className="px-4 py-3 text-right">Net</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: PayrollItem) => (
                    <tr
                      key={item._id}
                      className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                      onClick={() =>
                        navigate(`/companies/${companyId}/payroll/runs/${runId}/items/${item._id}`)
                      }
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {getEmployeeName(item.employeeId)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {item.totals.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-red-600 dark:text-red-400">
                        {item.totals.totalDeductions > 0
                          ? `-${item.totals?.totalDeductions?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          : '0.00'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {(item.totals?.totalTax ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {(item.totals?.netPay ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {itemsData && itemsData.total > itemsData.limit && (
              <TablePaginationBar
                page={page}
                limit={itemsData.limit}
                total={itemsData.total}
                totalPages={Math.ceil(itemsData.total / itemsData.limit)}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {/* Confirm dialog */}
      {confirmAction && (
        <ConfirmDialog
          open
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
          title={confirmLabels[confirmAction].title}
          message={confirmLabels[confirmAction].message}
          confirmLabel={confirmLabels[confirmAction].label}
          variant={confirmAction === 'void' ? 'danger' : 'default'}
          confirmLoading={anyMutating}
        />
      )}

      {/* Reject modal */}
      <RejectReasonModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        title="Reject Payroll Run"
        description="This will revert the run to draft status. The submitter will be notified."
        loading={rejectMut.isPending}
      />

      <CreateExchangeRateModal
        open={exchangeRateTarget !== null}
        requirement={exchangeRateTarget}
        onClose={() => setExchangeRateTarget(null)}
        onCreated={() => {
          void handleExchangeRateCreated()
        }}
      />
    </PageShell>
  )
}

RunDetailPage.displayName = 'RunDetailPage'
